import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { authAPI } from "@/services/api";

interface User {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  company_type?: string;
  branch_id?: number | null;
  branch_name?: string | null;
  // Computed by the backend from SYSTEM_ADMIN_EMAILS.
  is_system_admin?: boolean;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isAdmin: boolean;
  isSystemAdmin: boolean;
  loading: boolean;
}

const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  isAdmin: false,
  isSystemAdmin: false,
  loading: true,
});

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  // Seed from cache immediately so components never see null after login
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem('stockwise-user-cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('token');
    try {
      if (!token) {
        setLoading(false);
        return;
      }

      const userData = await authAPI.getCurrentUser();
      // A fresh login may have replaced the token while this request was in
      // flight; its response must not overwrite the new session's profile.
      if (localStorage.getItem('token') !== token) return;
      localStorage.setItem('stockwise-user-cache', JSON.stringify(userData));
      setUser(userData);
    } catch (error: any) {
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // Only remove the token that was actually rejected. If the user has
        // logged in again in the meantime, keep the new session intact.
        if (localStorage.getItem('token') === token) {
          console.log('Token is invalid (401), removing and logging out');
          localStorage.removeItem('token');
          localStorage.removeItem('stockwise-user-cache');
          setUser(null);
          // Dispatch auth change event to update App.tsx state
          window.dispatchEvent(new CustomEvent('authStateChange', { detail: false }));
        }
      } else {
        // For other errors (500, network issues, etc.), don't remove the token
        // and don't dispatch auth change events
        console.warn('Failed to fetch user data, but keeping token and auth state:', error.message);
        // Keep existing user data if available, don't clear it
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();

    // Listen for auth state changes
    const handleAuthChange = (event: CustomEvent) => {
      if (event.detail) {
        // User logged in, fetch user data
        fetchCurrentUser();
      } else {
        // User logged out
        setUser(null);
        localStorage.removeItem('stockwise-user-cache');
        setLoading(false);
      }
    };

    window.addEventListener('authStateChange', handleAuthChange as EventListener);

    return () => {
      window.removeEventListener('authStateChange', handleAuthChange as EventListener);
    };
  }, []);

  const isAdmin = user?.role === 'admin';
  const isSystemAdmin = user?.is_system_admin === true;

  return (
    <UserContext.Provider value={{ user, setUser, isAdmin, isSystemAdmin, loading }}>
      {children}
    </UserContext.Provider>
  );
};